import React from 'react';
import { Line } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';

import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import 'chartjs-adapter-moment';
import { asArray, getDataValue, getEntryTimestamp, limitRows, toFiniteNumber } from '../utils';

ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler
);

const colourPalette = [
  '#FF6384',
  '#36A2EB',
  '#FFCE56',
  '#4BC0C0',
  '#9966FF',
  '#FF9F40',
  '#C9CBCF',
  '#8B0000',
  '#00AA00',
  '#0000FF',
];

const Settings = {
  Name: 'Chart',
  Display: true,
  options: {
    view: null,
    settings: [
      { id: 'number', type: 'number', name: 'Number of data points', min: 1, default: 300 },
      { id: 'zero', type: 'switch', name: 'Begin from zero' },
      { id: 'scale', type: 'switch', name: 'Same scale' },
      {
        id: 'datasets',
        type: 'array',
        settings: [
          { id: 'id', type: 'text', name: 'Data path' },
          { id: 'label', type: 'text', name: 'Label' },
          //{ id: 'fill', type: 'switch', name: 'Fill area' },
        ],
      },
    ],
    grid: true,
  },
};

function buildDatasets(DeviceData, userSettings) {
  const rows = limitRows(DeviceData, userSettings.number || 300).reverse();
  const configuredDatasets = asArray(userSettings.datasets).filter((row) => row?.id);

  return configuredDatasets.map((element, index) => {
    const colour = colourPalette[index % colourPalette.length];
    const sameScale = userSettings.scale === true;
    const yAxisID = sameScale ? 'y' : `y${index}`;

    return {
      id: element.id,
      label: element.label || element.id,
      data: rows.map((row) => {
        const timestamp = getEntryTimestamp(row);
        const rawValue = getDataValue(row, element.id);
        const value = toFiniteNumber(rawValue);

        return {
          x: timestamp ? new Date(timestamp) : null,
          y: value,
        };
      }).filter((point) => point.x),
      fill: element.fill === true,
      backgroundColor: colour,
      borderColor: colour,
      tension: 0.2,
      yAxisID,
      spanGaps: false,
    };
  });
}

const View = ({ DeviceData, Settings: userSettings = {} }) => {
  if (!asArray(DeviceData).length) {
    return <Box>No data.</Box>;
  }

  const datasets = buildDatasets(DeviceData, userSettings);

  if (!datasets.length) {
    return <Box>Chart data paths are not configured.</Box>;
  }

  const sameScale = userSettings.scale === true;
  const beginAtZero = userSettings.zero === true;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      tooltip: { enabled: true },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          displayFormats: {
            millisecond: 'HH:mm:ss.SSS',
            second: 'HH:mm:ss',
            minute: 'HH:mm',
            hour: 'MMM D, HH:mm',
            day: 'MMM D',
            week: 'll',
            month: 'MMM YYYY',
            quarter: '[Q]Q - YYYY',
            year: 'YYYY',
          },
        },
        title: { display: true, text: 'Time' },
      },
    },
  };

  if (sameScale) {
    options.scales.y = {
      type: 'linear',
      position: 'left',
      beginAtZero,
      title: { display: true, text: 'Value' },
    };
  } else {
    datasets.forEach((dataset, index) => {
      options.scales[dataset.yAxisID] = {
        type: 'linear',
        position: index % 2 === 0 ? 'left' : 'right',
        beginAtZero,
        title: { display: true, text: dataset.label },
        grid: { drawOnChartArea: index === 0 },
      };
    });
  }

  const hasAnyPoints = datasets.some((dataset) => dataset.data.some((point) => point.y !== null));

  if (!hasAnyPoints) {
    return <Box>No numeric chart data available.</Box>;
  }

  return (
    <Box sx={{ height: 320, width: '100%' }}>
      <Line data={{ datasets }} options={options} />
      {!hasAnyPoints ? <Typography variant="body2">No numeric data to display.</Typography> : null}
    </Box>
  );
};

export default {
  Settings,
  View,
};
